import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";

const SECTIONS = [
  {
    key: "home_hero",
    title: "Hero principal (Home)",
    description: "Vídeo de fondo, mensaje y CTAs de la portada",
    fields: [
      { name: "video_url", label: "URL del vídeo (mp4)", type: "url" },
      { name: "poster_url", label: "Imagen poster (fallback antes de cargar el vídeo)", type: "url" },
      { name: "overline", label: "Overline (sobre el título)" },
      { name: "title_part1", label: "Título — parte 1" },
      { name: "title_emphasis", label: "Título — palabra destacada (cursiva oro)" },
      { name: "title_part2", label: "Título — parte 2 (línea inferior)" },
      { name: "subtitle", label: "Subtítulo", type: "textarea" },
      { name: "cta_primary_label", label: "Botón principal — texto" },
      { name: "cta_primary_link", label: "Botón principal — enlace" },
      { name: "cta_secondary_label", label: "Botón secundario — texto" },
      { name: "cta_secondary_link", label: "Botón secundario — enlace" },
    ],
  },
  {
    key: "home_manifesto",
    title: "Sección Manifiesto (Home)",
    description: "Bloque editorial split image+texto",
    fields: [
      { name: "overline", label: "Overline" },
      { name: "title_part1", label: "Título parte 1" },
      { name: "title_emphasis", label: "Palabra cursiva oro" },
      { name: "title_part2", label: "Título parte 2" },
      { name: "body", label: "Cuerpo", type: "textarea" },
      { name: "image_url", label: "URL imagen", type: "url" },
    ],
  },
  {
    key: "site_meta",
    title: "Metadatos del sitio",
    description: "Nombre y tagline general",
    fields: [
      { name: "site_name", label: "Nombre del sitio" },
      { name: "tagline", label: "Tagline" },
    ],
  },
];

export default function AdminSiteConfig() {
  const [config, setConfig] = useState({});
  const [activeKey, setActiveKey] = useState(SECTIONS[0].key);

  const load = () => api.get("/admin/site-config").then(r => setConfig(r.data || {}));
  useEffect(() => { load(); }, []);

  const updateField = (sectionKey, fieldName, val) => {
    setConfig(c => ({ ...c, [sectionKey]: { ...(c[sectionKey] || {}), [fieldName]: val } }));
  };

  const saveSection = async (sectionKey) => {
    try {
      await api.put(`/admin/site-config/${sectionKey}`, { value: config[sectionKey] || {} });
      toast.success("Configuración guardada");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const activeSection = SECTIONS.find(s => s.key === activeKey);
  const data = config[activeKey] || {};

  return (
    <div data-testid="admin-site-config">
      <p className="overline mb-3">Configuración del sitio</p>
      <h1 className="font-heading text-4xl text-ivory mb-3">Editor de páginas</h1>
      <p className="text-sm text-ivory/60 mb-8 max-w-2xl">Modifica textos, imágenes y vídeos de cada sección. Los cambios se aplican en vivo al recargar la home pública.</p>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        <aside className="lg:col-span-1 space-y-1">
          {SECTIONS.map(s => (
            <button
              key={s.key}
              onClick={() => setActiveKey(s.key)}
              data-testid={`section-${s.key}`}
              className={`w-full text-left px-4 py-3 text-xs uppercase tracking-[0.15em] border-l-2 transition-colors ${
                activeKey === s.key ? "border-gold text-gold bg-graphite" : "border-transparent text-ivory/60 hover:text-ivory hover:bg-graphite/50"
              }`}
            >
              {s.title}
            </button>
          ))}
        </aside>

        <div className="lg:col-span-3">
          <div className="bg-graphite border border-smoke p-8">
            <h2 className="font-heading text-2xl text-ivory mb-2">{activeSection.title}</h2>
            <p className="text-sm text-ivory/50 mb-6">{activeSection.description}</p>

            <div className="space-y-5">
              {activeSection.fields.map(f => (
                <div key={f.name}>
                  <label className="luxe-label">{f.label}</label>
                  {f.type === "textarea" ? (
                    <textarea
                      data-testid={`field-${activeKey}-${f.name}`}
                      value={data[f.name] || ""}
                      onChange={e => updateField(activeKey, f.name, e.target.value)}
                      className="luxe-input border border-smoke px-3 py-3 min-h-[100px]"
                    />
                  ) : (
                    <input
                      data-testid={`field-${activeKey}-${f.name}`}
                      type={f.type || "text"}
                      value={data[f.name] || ""}
                      onChange={e => updateField(activeKey, f.name, e.target.value)}
                      className="luxe-input"
                    />
                  )}
                  {(f.name.includes("url") || f.name.includes("image")) && data[f.name] && (
                    <div className="mt-2">
                      {f.name.includes("video") ? (
                        <video src={data[f.name]} className="h-24 border border-smoke" muted />
                      ) : (
                        <img src={data[f.name]} alt="" className="h-24 border border-smoke object-cover" />
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="flex gap-3 mt-8 pt-6 border-t border-smoke">
              <button onClick={() => saveSection(activeKey)} className="btn-gold" data-testid={`save-${activeKey}`}>Guardar sección</button>
              <a href="/" target="_blank" rel="noreferrer" className="btn-outline-gold">Ver en vivo ↗</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
